import {
  AS400_SUPPLIER_COLORS,
  as400ColumnEditKey,
  as400SupplierNumberOf,
  type As400ExportColumn,
} from '@erp/rpc';
import { renderSortToggle } from './as400GridSortToggle';

/**
 * The Purchase Data worksheet's three supplier groups on the review grid
 * (GEN-2778): a "Supplier N" band above each run of a supplier's columns,
 * those headers washed in the supplier's colour, and a divider in that colour
 * either side of the run. Cell bodies are left alone — their backgrounds are
 * the edit-state tints (pending, staged, before, carried), and the dividers
 * are inset shadows that sit beside a cell's rule outline rather than over it.
 *
 * Handsontable renders headers and cells outside React, so all of this is
 * inline style, applied as each one draws.
 */

export type SupplierNumber = 1 | 2 | 3;

/** Each grid column's supplier, positional; undefined for no supplier's. */
export function supplierNumbersOf(
  columns: readonly As400ExportColumn[],
): (SupplierNumber | undefined)[] {
  return columns.map((column) =>
    as400SupplierNumberOf(as400ColumnEditKey(column)),
  );
}

/** One header in the band row: a label spanning `colspan` columns. */
export interface SupplierBandHeader {
  label: string;
  colspan: number;
}

/**
 * The band row above the column headers, as a Handsontable `nestedHeaders`
 * row: one header per run of adjacent columns of one supplier, labelled by
 * `labelFor`, and an empty one per column that is no supplier's. A run is cut
 * at the frozen-column edge, since a header can't span the frozen overlay and
 * the scrolling part both. Undefined when no column is a supplier's, so every
 * other worksheet keeps its single header row.
 */
export function supplierBandRow(
  suppliers: readonly (SupplierNumber | undefined)[],
  fixedColumns: number,
  labelFor: (supplier: SupplierNumber) => string,
): SupplierBandHeader[] | undefined {
  if (suppliers.every((supplier) => supplier === undefined)) return undefined;
  const band: SupplierBandHeader[] = [];
  let index = 0;
  while (index < suppliers.length) {
    const supplier = suppliers[index];
    let end = index + 1;
    if (supplier !== undefined) {
      while (
        end < suppliers.length &&
        suppliers[end] === supplier &&
        end !== fixedColumns
      ) {
        end += 1;
      }
    }
    band.push({
      label: supplier === undefined ? '' : labelFor(supplier),
      colspan: end - index,
    });
    index = end;
  }
  return band;
}

// How strongly a supplier's colour washes its band and its column headers;
// translucent, so the header's own text colour reads in either theme.
const BAND_WASH = 35;
const HEADER_WASH = 15;
const DIVIDER_WIDTH = '2px';

// A translucent layer over the header's own background rather than a
// replacement for it, so Handsontable's highlight of a selected column's
// header still shows through.
function wash(supplier: SupplierNumber, percent: number): string {
  const color = `color-mix(in srgb, ${AS400_SUPPLIER_COLORS[supplier].solid} ${percent}%, transparent)`;
  return `linear-gradient(${color}, ${color})`;
}

/**
 * The divider(s) at a column of a supplier's run: on its start side when the
 * column before is not the same supplier's, on its end side when the column
 * after is not. Empty for a column that is no supplier's.
 */
export function supplierDividerShadow(
  suppliers: readonly (SupplierNumber | undefined)[],
  col: number,
): string {
  const supplier = suppliers[col];
  if (supplier === undefined) return '';
  const color = AS400_SUPPLIER_COLORS[supplier].solid;
  const shadows: string[] = [];
  if (suppliers[col - 1] !== supplier) {
    shadows.push(`inset ${DIVIDER_WIDTH} 0 0 ${color}`);
  }
  if (suppliers[col + 1] !== supplier) {
    shadows.push(`inset -${DIVIDER_WIDTH} 0 0 ${color}`);
  }
  return shadows.join(', ');
}

/**
 * Add a column's supplier divider to a cell that has just drawn: after the
 * renderer, which sets (or clears) the cell's rule outline, also a shadow, so
 * the two are joined rather than one replacing the other.
 */
export function paintSupplierCell(
  td: HTMLTableCellElement,
  suppliers: readonly (SupplierNumber | undefined)[],
  col: number,
): void {
  const divider = supplierDividerShadow(suppliers, col);
  if (!divider) return;
  td.style.boxShadow = td.style.boxShadow
    ? `${td.style.boxShadow}, ${divider}`
    : divider;
}

/**
 * The custom property a band label's sticky inset reads: the width of the
 * frozen columns (and row gutter) covering the grid's start edge. It is set on
 * the grid's wrapper by {@link syncSupplierBandInset}, and the labels inherit
 * it rather than measuring anything as they draw.
 */
export const SUPPLIER_BAND_INSET_VAR = '--as400-supplier-band-inset';

/**
 * Wash a header in its column's supplier colour, with the run's dividers; the
 * band header (`band`) a deeper wash, spanning its whole run so both sides get
 * one. Handsontable recycles header cells, so a header that is no supplier's
 * is cleared rather than left alone.
 *
 * A band's label is sticky: it rides along its run as the grid scrolls, the
 * frozen columns' width ({@link SUPPLIER_BAND_INSET_VAR}) in from the grid's
 * start, so whichever supplier's columns are on screen stay named, not only
 * the run's first one.
 *
 * This runs for every header cell on every redraw, scrolling included, so it
 * only writes: a layout read here (an `offsetWidth`) forces a style and layout
 * pass per header cell, which is what made scrolling Purchase Data crawl.
 */
export function paintSupplierHeader(
  th: HTMLTableCellElement,
  suppliers: readonly (SupplierNumber | undefined)[],
  col: number,
  band: boolean,
): void {
  const supplier = suppliers[col];
  const labelled = band && supplier !== undefined;
  th.style.textAlign = labelled ? 'left' : '';
  th.style.fontWeight = labelled ? '600' : '';
  // Handsontable clips header cells; a clipping header would be the label's
  // sticky container instead of the scrolling grid.
  th.style.overflow = labelled ? 'visible' : '';
  const label = th.querySelector<HTMLElement>('.colHeader');
  if (label) {
    label.style.position = labelled ? 'sticky' : '';
    label.style.setProperty(
      'inset-inline-start',
      labelled ? `var(${SUPPLIER_BAND_INSET_VAR}, 0px)` : '',
    );
    label.style.paddingInline = labelled ? '6px' : '';
  }
  if (supplier === undefined) {
    th.style.backgroundImage = '';
    th.style.boxShadow = '';
    return;
  }
  th.style.backgroundImage = wash(supplier, band ? BAND_WASH : HEADER_WASH);
  if (band) {
    const color = AS400_SUPPLIER_COLORS[supplier].solid;
    th.style.boxShadow = `inset ${DIVIDER_WIDTH} 0 0 ${color}, inset -${DIVIDER_WIDTH} 0 0 ${color}`;
    return;
  }
  th.style.boxShadow = supplierDividerShadow(suppliers, col);
}

/**
 * Paint a band header: its supplier's wash, with its label pinned just past
 * the frozen columns. Band cells are recycled with the column headers', so
 * the sort chevron is cleared off them too.
 */
export function paintSupplierBandHeader(
  th: HTMLTableCellElement,
  suppliers: readonly (SupplierNumber | undefined)[],
  col: number,
): void {
  paintSupplierHeader(th, suppliers, col, true);
  renderSortToggle(th, col, undefined, '');
}

/**
 * Measure the frozen columns (and row gutter) covering the grid's start edge,
 * found under `grid` as Handsontable's inline-start overlay, into
 * {@link SUPPLIER_BAND_INSET_VAR} on `grid`. Run from the grid's
 * `afterRender`, once per full render: the frozen width changes only with the
 * column widths, which a scroll never changes.
 */
export function syncSupplierBandInset(grid: HTMLElement | null): void {
  if (!grid) return;
  const frozen = grid.querySelector<HTMLElement>('.ht_clone_inline_start');
  const inset = `${frozen?.offsetWidth ?? 0}px`;
  if (grid.style.getPropertyValue(SUPPLIER_BAND_INSET_VAR) !== inset) {
    grid.style.setProperty(SUPPLIER_BAND_INSET_VAR, inset);
  }
}
